// components/ErrorBoundary.jsx
// AWS/Google-style error boundary with comprehensive logging and fallback UI
import React from "react";

/**
 * Production-grade error boundary
 * Catches React component errors and displays user-friendly fallback
 * Logs detailed error info to console for debugging
 *
 * Usage:
 * <ErrorBoundary componentName="CompanyDocuments">
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const { componentName = "Unknown Component" } = this.props;

    // Increment error count
    this.setState((prev) => ({
      error,
      errorInfo,
      errorCount: prev.errorCount + 1,
    }));

    // Log comprehensive error details
    console.error("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.error(`🔴 ERROR BOUNDARY CAUGHT ERROR IN: ${componentName}`);
    console.error("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.error("Error:", error);
    console.error("Error Message:", error?.message);
    console.error("Error Stack:", error?.stack);
    console.error("Component Stack:", errorInfo?.componentStack);
    console.error("Props:", this.props);
    console.error("State:", this.state);
    console.error("Timestamp:", new Date().toISOString());
    console.error("User Agent:", navigator.userAgent);
    console.error("URL:", window.location.href);
    console.error("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

    // Send to error tracking service (e.g., Sentry, LogRocket)
    if (typeof window !== "undefined" && window.Sentry) {
      const sentry = window.Sentry;
      const capture = () =>
        sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack,
              componentName,
            },
          },
        });

      if (typeof sentry.withScope === "function") {
        sentry.withScope((scope) => {
          scope.setTag("react.error_boundary", componentName);
          scope.setContext("react", {
            componentStack: errorInfo.componentStack,
            componentName,
          });
          capture();
        });
      } else {
        capture();
      }
    }

    // Could also send to your backend logging endpoint
    // this.sendErrorLog(error, errorInfo, componentName);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { componentName = "This component", fallback } = this.props;
      const { error, errorInfo, errorCount } = this.state;

      // If custom fallback provided, use it
      if (fallback) {
        return fallback;
      }

      // AWS/Google-style error UI
      return (
        <div
          style={{
            padding: "40px 20px",
            maxWidth: "800px",
            margin: "0 auto",
            backgroundColor: "#fef2f2",
            border: "2px solid #fca5a5",
            borderRadius: "12px",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          }}>
          {/* Error Icon */}
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                backgroundColor: "#ef4444",
                color: "white",
                fontSize: "32px",
                fontWeight: "bold",
                marginBottom: "16px",
              }}>
              !
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: "24px",
                fontWeight: "700",
                color: "#991b1b",
              }}>
              Something Went Wrong
            </h1>
          </div>

          {/* Error Message */}
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              marginBottom: "24px",
              border: "1px solid #fecaca",
            }}>
            <p
              style={{
                margin: "0 0 12px 0",
                fontSize: "16px",
                color: "#7c2d12",
                fontWeight: "600",
              }}>
              {componentName} encountered an error and couldn't display properly.
            </p>
            <p
              style={{
                margin: "0 0 16px 0",
                fontSize: "14px",
                color: "#92400e",
              }}>
              Don't worry - your data is safe. This is a display issue only.
            </p>

            {/* Technical Details (collapsible) */}
            <details
              style={{
                marginTop: "16px",
                padding: "12px",
                backgroundColor: "#fef2f2",
                borderRadius: "6px",
                border: "1px solid #fecaca",
              }}>
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: "600",
                  color: "#7c2d12",
                  fontSize: "14px",
                }}>
                Technical Details (for developers)
              </summary>
              <div
                style={{
                  marginTop: "12px",
                  fontSize: "12px",
                  fontFamily: "monospace",
                  color: "#92400e",
                }}>
                <p style={{ margin: "4px 0" }}>
                  <strong>Error:</strong> {error?.message || "Unknown error"}
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>Component:</strong> {componentName}
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>Error Count:</strong> {errorCount}
                </p>
                {errorInfo && (
                  <details style={{ marginTop: "8px" }}>
                    <summary style={{ cursor: "pointer" }}>Stack Trace</summary>
                    <pre
                      style={{
                        marginTop: "8px",
                        padding: "8px",
                        backgroundColor: "white",
                        border: "1px solid #fecaca",
                        borderRadius: "4px",
                        overflow: "auto",
                        fontSize: "11px",
                      }}>
                      {errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            </details>
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
              flexWrap: "wrap",
            }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                borderRadius: "8px",
                border: "2px solid #dc2626",
                backgroundColor: "#dc2626",
                color: "white",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#b91c1c";
                e.target.style.borderColor = "#b91c1c";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#dc2626";
                e.target.style.borderColor = "#dc2626";
              }}>
              Try Again
            </button>

            <button
              onClick={this.handleReload}
              style={{
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                borderRadius: "8px",
                border: "2px solid #dc2626",
                backgroundColor: "white",
                color: "#dc2626",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#fef2f2";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "white";
              }}>
              Reload Page
            </button>

            <button
              onClick={() => window.history.back()}
              style={{
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                borderRadius: "8px",
                border: "2px solid #9ca3af",
                backgroundColor: "white",
                color: "#6b7280",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f3f4f6";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "white";
              }}>
              Go Back
            </button>
          </div>

          {/* Support Message */}
          <div
            style={{
              marginTop: "24px",
              padding: "16px",
              backgroundColor: "white",
              borderRadius: "8px",
              border: "1px solid #fecaca",
              textAlign: "center",
            }}>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#92400e",
              }}>
              💡 <strong>For Support:</strong> Please copy the technical details above and contact your administrator. Include the URL, what you were doing, and the error message.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
